import { Percent, TrendingUp, Settings as SettingsIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const TIER_RATES = [
    { name: 'Bronze',   rate: '0.5%', color: '#CD7F32' },
    { name: 'Silver',   rate: '1.0%', color: '#C0C0C0' },
    { name: 'Gold',     rate: '1.5%', color: '#FFD700' },
    { name: 'Platinum', rate: '2.0%', color: '#E5E4E2' },
    { name: 'Diamond',  rate: '3.0%', color: '#B9F2FF' },
];

export const CashbackAdminTab = ({
    cashbackStats,
    cashbackProcessing,
    cashbackResult,
    handleProcessCashback,
    settings,
    handleSettingsUpdate,
}) => (
    <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="card-dark p-6 text-center">
                <Percent className="w-8 h-8 text-[#00FFFF] mx-auto mb-2" />
                <p className="text-[#A0AAB2] text-sm">This Week Paid</p>
                <p className="text-2xl font-mono font-bold text-white">{(cashbackStats?.this_week?.total || 0).toFixed(2)}</p>
                <p className="text-[#A0AAB2] text-xs mt-1">{cashbackStats?.this_week?.count || 0} users</p>
            </div>
            <div className="card-dark p-6 text-center">
                <TrendingUp className="w-8 h-8 text-[#39FF14] mx-auto mb-2" />
                <p className="text-[#A0AAB2] text-sm">All Time Paid</p>
                <p className="text-2xl font-mono font-bold text-white">{(cashbackStats?.all_time?.total || 0).toFixed(2)}</p>
                <p className="text-[#A0AAB2] text-xs mt-1">{cashbackStats?.all_time?.count || 0} payouts</p>
            </div>
            <div className="card-dark p-6 flex flex-col items-center justify-center">
                <p className="text-[#A0AAB2] text-sm mb-3">Process Weekly Cashback</p>
                <Button
                    onClick={handleProcessCashback}
                    disabled={cashbackProcessing}
                    className="bg-[#00FFFF] text-black hover:bg-[#00FFFF]/80"
                    data-testid="process-cashback-btn"
                >
                    {cashbackProcessing ? 'Processing...' : 'Run Cashback Payout'}
                </Button>
                {cashbackResult && (
                    <div className={`mt-3 text-sm text-center ${cashbackResult.error ? 'text-[#FF3B30]' : 'text-[#39FF14]'}`}>
                        {cashbackResult.error
                            ? cashbackResult.error
                            : `Paid ${cashbackResult.total_cashback_paid?.toFixed(2)} to ${cashbackResult.users_processed} users`
                        }
                    </div>
                )}
            </div>
        </div>

        <div className="card-dark p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <SettingsIcon className="w-5 h-5 text-[#00FFFF]" />
                Cashback Settings
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-center justify-between">
                    <div>
                        <Label className="text-white">Cashback Enabled</Label>
                        <p className="text-xs text-[#A0AAB2]">Enable weekly VIP cashback</p>
                    </div>
                    <Switch
                        checked={settings.vip?.cashback_enabled !== false}
                        onCheckedChange={(v) => handleSettingsUpdate('vip', 'cashback_enabled', v)}
                        data-testid="cashback-enabled-toggle"
                    />
                </div>
                <div className="flex items-center justify-between">
                    <div>
                        <Label className="text-white">Auto Cashback</Label>
                        <p className="text-xs text-[#A0AAB2]">Automatically pay out on schedule</p>
                    </div>
                    <Switch
                        checked={settings.vip?.auto_cashback || false}
                        onCheckedChange={(v) => handleSettingsUpdate('vip', 'auto_cashback', v)}
                        data-testid="auto-cashback-toggle"
                    />
                </div>
                <div>
                    <Label className="text-white">Min Loss for Cashback</Label>
                    <Input
                        type="number"
                        value={settings.vip?.cashback_min_loss || 100}
                        onChange={(e) => handleSettingsUpdate('vip', 'cashback_min_loss', parseFloat(e.target.value) || 100)}
                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                        data-testid="cashback-min-loss"
                    />
                </div>
                <div>
                    <Label className="text-white">Payout Day</Label>
                    <Select
                        value={String(settings.vip?.cashback_day ?? 0)}
                        onValueChange={(v) => handleSettingsUpdate('vip', 'cashback_day', parseInt(v))}
                    >
                        <SelectTrigger className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="cashback-day-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10">
                            {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => (
                                <SelectItem key={i} value={String(i)} className="text-white hover:bg-white/10">{d}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </div>

        <div className="card-dark p-6">
            <h3 className="text-lg font-bold text-white mb-4">VIP Cashback Rates</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {TIER_RATES.map((tier) => (
                    <div key={tier.name} className="p-3 rounded-lg bg-white/5 text-center border" style={{ borderColor: tier.color + '40' }}>
                        <p className="font-bold text-sm" style={{ color: tier.color }}>{tier.name}</p>
                        <p className="text-white text-lg font-mono">{tier.rate}</p>
                    </div>
                ))}
            </div>
        </div>
    </div>
);
